import { localStorageService } from 'services/localStorage';

import { environments, supportedLocales } from 'shared/constants';
import { copy, guid, logError, logInfo } from 'shared/functions';

import { requestBackend } from 'utils/request';

const reactElement = Symbol.for('react.element');

export const sourceTranslate = () => {
  const err = new Error();
  const stackLines = err.stack.split('\n');
  return `${stackLines[4]}, ${stackLines[5]}, ${stackLines[6]}`;
};

export const render = (translation: any, options: any) => {
  if (!options) {
    return translation;
  }

  const processedElements = {};
  const preparedTranslation = translation.replace(/\{\{(.*?)\}\}/g, (_match: any, preToken: any) => {
    const token = preToken.trim();
    const optionsToken = options[token];
    if (optionsToken && typeof optionsToken === 'object') {
      if (optionsToken.$$typeof === reactElement) {
        const key = guid();
        processedElements[key] = optionsToken;
        return key;
      }
      return `# Error: ${token} #`;
    }
    return optionsToken;
  });
  const elements = Object.keys(processedElements).reduce(
    (prev, cur) =>
      prev.flatMap((block) => {
        if (typeof block !== 'string') {
          return block;
        }
        const splittedBlock = block.split(cur);
        return splittedBlock.flatMap((subBlock, index) =>
          index === splittedBlock.length - 1 ? subBlock : [subBlock, processedElements[cur]],
        );
      }),
    [preparedTranslation],
  );
  return elements.length === 1 ? (
    elements[0]
  ) : (
    <span key={translation}>
      {elements.map((element, index) => (
        <span key={index}>{element}</span>
      ))}
    </span>
  );
};

export const translate = (literal: string, options: AnyValue, translations: Record<string, string>) => {
  const locale = localStorageService.getItem('locale');

  if (typeof literal !== 'string') {
    logError(`literal: ${literal} is not string`, { literal, options });
    return;
  }

  const isValidLiteral = !!literal.trim() && literal.trim() === literal;

  if (!isValidLiteral) {
    logError(`Translate called with invalid literal: ${literal}`, { literal, options });
    return;
  }

  const translated = translations[literal];

  if (translated) {
    return translated;
  }

  const localePrefix = locale && locale !== supportedLocales.en ? `${locale.toUpperCase()} - ` : '';
  const literalWithPrefix = `${localePrefix}${literal}`;
  translations[literal] = literalWithPrefix;

  const environment = process.env.ENVIRONMENT || process.env.REACT_APP_ENVIRONMENT;

  const isDevEnv = [environments.dev, environments.uat, environments.test].includes(environment);
  const isCreatedLiteral = localStorageService.processedLiterals.includes(literal);

  if (isDevEnv && !isCreatedLiteral) {
    const source = sourceTranslate();
    logInfo(`Literal ${literal} not found`);
    requestBackend('/translations/public/literals', {
      method: 'POST',
      body: JSON.stringify({
        literal,
        source,
      }),
    })
      .then(() => {
        localStorageService.processedLiterals = [...localStorageService.processedLiterals, literal];
      })
      .catch((err) => {
        logError(err, {
          message: `Failed to create literal ${literal}`,
        });
      });
  }

  return literalWithPrefix;
};

export const connectTranslations = (localTranslations: any) => {
  window.translationsReady = true;

  if (localStorageService.getItem('disableTranslations') === 'true') {
    return;
  }

  const translations = copy(localTranslations);

  window.translate = (literal: string, options: AnyValue) => {
    const translatedLiteral = translate(literal, options, translations);
    return render(translatedLiteral, options);
  };
};
